import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ShoppingProvider, useShopping } from './contexts/ShoppingContext';
import { CheckoutProvider } from './contexts/CheckoutContext';
import { UserProvider } from './contexts/UserContext';
import { ClientDataProvider } from './contexts/ClientDataContext';
import { Header } from './components/Header';
import { CategoryNav } from './components/CategoryNav';
import { ProductGrid } from './components/ProductGrid';
import { Cart } from './components/Cart';
import { CartPage } from './components/CartPage';
import { QuickViewModal } from './components/QuickViewModal';
import { ProductPage } from './components/ProductPage';
import { Checkout } from './components/Checkout';
import { OrderConfirmation } from './components/OrderConfirmation';
import { Toast } from './components/ui';
import { HelpButton } from './components/HelpButton';
import { FeedbackButton } from './components/FeedbackButton';
import { WelcomeBanner } from './components/WelcomeBanner';
import { SanctumService } from './services/sanctumService';
import { usePreviewMode } from './hooks/usePreviewMode';
import { useGuestMode } from './hooks/useGuestMode';

const authCheckoutEnabled = import.meta.env.VITE_AUTH_CHECKOUT_ENABLED === 'true';

function AppContent() {
  const { state, dispatch, loading } = useShopping();
  const location = useLocation();
  const { isPreviewMode, showWelcomeBanner, dismissWelcomeBanner, disable: disablePreviewMode } = usePreviewMode();
  const { isGuest, loading: userLoading } = useGuestMode();

  // Don't show CategoryNav on cart, checkout, or order confirmation pages
  const showCategoryNav = !['/cart', '/checkout'].includes(location.pathname) &&
    !location.pathname.startsWith('/order-confirmation');

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
        Skip to main content
      </a>
      <Header />
      {!loading && !isGuest && showWelcomeBanner && (
        <WelcomeBanner
          onDismiss={dismissWelcomeBanner}
          onExitPreview={disablePreviewMode}
          showCategoryNav={showCategoryNav}
        />
      )}
      <div className="flex flex-1 overflow-hidden">
        {showCategoryNav && <CategoryNav />}
        <main id="main-content" role="main" className={`flex-1 overflow-y-auto ${showCategoryNav ? 'md:ml-56' : ''}`}>
          <Routes>
            <Route path="/" element={<ProductGrid />} />
            <Route path="/product/:productId" element={<ProductPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route
              path="/checkout"
              element={
                userLoading ? null :
                (!isGuest && !authCheckoutEnabled) ? <Navigate to="/cart" replace /> :
                <CheckoutProvider>
                  <Checkout />
                </CheckoutProvider>
              }
            />
            <Route path="/order-confirmation/:masterOrderNo" element={<OrderConfirmation />} />
          </Routes>
        </main>
      </div>
      <Cart />
      <QuickViewModal />

        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3">
            {isPreviewMode && <FeedbackButton />}
            <HelpButton />
        </div>

      {state.toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          productName={toast.productName}
          productImage={toast.productImage}
          type={toast.type}
          onClose={() => dispatch({ type: 'REMOVE_TOAST', payload: toast.id })}
        />
      ))}
    </div>
  );
}

function App() {
  // Initialize CSRF token when app loads
  // This fetches the XSRF-TOKEN cookie from Laravel Sanctum which is required
  // for all POST/PUT/DELETE requests to prevent CSRF attacks (419 errors)
  useEffect(() => {
    SanctumService.initCsrf();
  }, []);

  return (
    <UserProvider>
      <ShoppingProvider>
        <ClientDataProvider>
          <AppContent />
        </ClientDataProvider>
      </ShoppingProvider>
    </UserProvider>
  );
}

export default App;